import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  LifeBuoy,
  Plus,
  Search,
  MessageSquare,
  ChevronRight,
  ChevronDown,
  Store,
  SlidersHorizontal,
  Check,
  List as ListIcon,
  Columns3,
  Calendar as CalendarIcon,
} from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { usePageTranslation } from "@/i18n/useTranslation";
import { useLanguage } from "@/i18n/useLanguage";
import { supportTranslations as enSupport } from "@/i18n/locales/en/support";
import { supportTranslations as thSupport } from "@/i18n/locales/th/support";
import {
  listMyTickets,
  listIncoming,
  listIncomingTenants,
  isSupportReceiver,
} from "./api/supportApi";
import { useAuthStore } from "@/lib/store/authStore";
import { SupportPresence } from "./components/SupportPresence";
import { SupportBoard } from "./components/SupportBoard";
import { SupportCalendar } from "./components/SupportCalendar";
import {
  PriorityBadge,
  StatusPill,
  categoryLabel,
  priorityLabel,
  relativeTime,
  statusLabel,
} from "./components/meta";
import type { TenantGroup, TicketStatus, TicketSummary } from "./types";

type FilterKey = "All" | "Active" | TicketStatus;
type ViewKey = "list" | "board" | "calendar";
type T = typeof enSupport;

// Customer-facing statuses, and the "active work" default (hide Resolved+Closed).
const ALL_STATUSES: TicketStatus[] = [
  "Open",
  "In Progress",
  "Waiting on Customer",
  "Resolved",
  "Closed",
];
const ACTIVE_STATUSES: TicketStatus[] = ["Open", "In Progress", "Waiting on Customer"];
const sameSet = (a: TicketStatus[], b: TicketStatus[]) =>
  a.length === b.length && a.every((s) => b.includes(s));

const ALL_TENANTS_LABEL = "ทุกร้าน";

type TenantSel = { ids: string[]; label: string };

// Remember the john-inbox filter across navigation + reloads, so it doesn't reset
// to defaults every visit. Persisted per browser in localStorage.
const FILTER_STORAGE_KEY = "zvg.support.inboxFilter.v1";

function loadSavedFilter(): { tenant: TenantSel; statuses: TicketStatus[] } | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as { tenant?: TenantSel; statuses?: string[] };
    if (!p?.tenant || !Array.isArray(p.statuses)) return null;
    const statuses = p.statuses.filter((s): s is TicketStatus =>
      (ALL_STATUSES as string[]).includes(s),
    );
    const tenant: TenantSel = {
      ids: Array.isArray(p.tenant.ids)
        ? p.tenant.ids.filter((x) => typeof x === "string")
        : [],
      label: typeof p.tenant.label === "string" ? p.tenant.label : ALL_TENANTS_LABEL,
    };
    return { tenant, statuses };
  } catch {
    return null;
  }
}

// Thai labels for the private answering-side status (agent inbox only).
const INTERNAL_STATUS_TH: Record<string, string> = {
  New: "ใหม่",
  Investigating: "กำลังตรวจสอบ",
  Fixing: "กำลังแก้ไข",
  "Draft Ready": "ร่างพร้อมอนุมัติ",
  Approved: "อนุมัติ/ส่งแล้ว",
  Waiting: "รอลูกค้า",
  Done: "เสร็จ",
};

function matchesFilter(t: TicketSummary, f: FilterKey): boolean {
  if (f === "All") return true;
  if (f === "Active") return t.status !== "Resolved" && t.status !== "Closed";
  return t.status === f;
}

export function SupportPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = usePageTranslation({ en: enSupport, th: thSupport }) as T;
  const [filter, setFilter] = useState<FilterKey>("All");
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const currentView = ((searchParams.get("view") as ViewKey) || "list");
  const setView = (v: ViewKey) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("view", v);
        return next;
      },
      { replace: true },
    );
  const openCompose = () => navigate("/support/new");

  const FILTERS: { key: FilterKey; label: string }[] = [
    { key: "All", label: t.filterAll },
    { key: "Active", label: t.filterActive },
    { key: "Open", label: t.statusOpen },
    { key: "In Progress", label: t.statusInProgress },
    { key: "Waiting on Customer", label: t.statusWaiting },
    { key: "Resolved", label: t.statusResolved },
    { key: "Closed", label: t.statusClosed },
  ];

  // The support owner (john@zaviago.com) gets the cross-store receiving inbox:
  // ALL tenants' incoming tickets in one list. Every other user keeps the
  // normal tenant-isolated "my tickets" view — unchanged.
  const username = useAuthStore((s) => s.username);
  const isReceiver = isSupportReceiver(username);

  // ── john-inbox super-admin filters (server-side, so it scales) ──
  // Tenant = which business/stall(s) to show ([] = every tenant). Status = which
  // customer-facing statuses to KEEP; default hides Resolved+Closed (active work).
  const [tenantSel, setTenantSel] = useState<TenantSel>(
    () => loadSavedFilter()?.tenant ?? { ids: [], label: ALL_TENANTS_LABEL },
  );
  const [keptStatuses, setKeptStatuses] = useState<TicketStatus[]>(
    () => loadSavedFilter()?.statuses ?? ACTIVE_STATUSES,
  );

  // Persist the filter whenever it changes (john inbox only).
  useEffect(() => {
    if (!isReceiver || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        FILTER_STORAGE_KEY,
        JSON.stringify({ tenant: tenantSel, statuses: keptStatuses }),
      );
    } catch {
      // ignore storage quota / serialization errors
    }
  }, [isReceiver, tenantSel, keptStatuses]);

  const { data: tenantGroups } = useQuery({
    queryKey: ["support", "incoming", "tenants"],
    queryFn: listIncomingTenants,
    enabled: isReceiver,
  });

  const { data: tickets, isLoading } = useQuery({
    queryKey: isReceiver
      ? ["support", "incoming", tenantSel.ids, keptStatuses]
      : ["support", "tickets"],
    queryFn: isReceiver
      ? () => listIncoming({ tenants: tenantSel.ids, statuses: keptStatuses })
      : listMyTickets,
  });

  // Search applies to every view; the status chips only refine the list.
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (tickets ?? []).filter(
      (item) =>
        q === "" ||
        item.subject.toLowerCase().includes(q) ||
        item.name.toLowerCase().includes(q),
    );
  }, [tickets, search]);

  // For john, tenant + status are filtered on the SERVER; the client only does
  // the text search. Every other user keeps the local status-chip filter.
  const filtered = useMemo(
    () => (isReceiver ? searched : searched.filter((item) => matchesFilter(item, filter))),
    [searched, filter, isReceiver],
  );

  const activeCount = (tickets ?? []).filter(
    (item) => item.status !== "Resolved" && item.status !== "Closed",
  ).length;

  const VIEWS: { key: ViewKey; label: string; icon: typeof ListIcon }[] = [
    { key: "list", label: t.viewList, icon: ListIcon },
    { key: "board", label: t.viewBoard, icon: Columns3 },
    { key: "calendar", label: t.viewCalendar, icon: CalendarIcon },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <PageContainer>
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-white shadow-sm shadow-blue-500/20">
            <LifeBuoy className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t.title}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t.subtitle}</p>
          </div>
        </div>
        <Button className="rounded-full self-start sm:self-auto" onClick={openCompose}>
          <Plus className="h-4 w-4" />
          {t.newRequest}
        </Button>
      </div>

      {/* Live support presence */}
      <SupportPresence />

      {/* View switcher + search */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="inline-flex items-center gap-0.5 rounded-full bg-gray-100 p-0.5 dark:bg-gray-800">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
                currentView === v.key
                  ? "bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-100"
                  : "text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200",
              )}
            >
              <v.icon className="h-4 w-4" />
              {v.label}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder={t.searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Filters — list view only. John gets tenant + status dropdowns (server-side,
          scales to many customers); every other user keeps the simple status chips. */}
      {currentView === "list" && isReceiver && (
        <ReceiverFilterBar
          t={t}
          tenantGroups={tenantGroups ?? []}
          tenantSel={tenantSel}
          setTenantSel={setTenantSel}
          keptStatuses={keptStatuses}
          setKeptStatuses={setKeptStatuses}
          resultCount={filtered.length}
        />
      )}
      {currentView === "list" && !isReceiver && (
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={cn(
                "rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
                filter === f.key
                  ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700",
              )}
            >
              {f.label}
              {f.key === "Active" && activeCount > 0 && (
                <span className="ml-1.5 opacity-70">{activeCount}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-2xl border border-gray-200 p-4 dark:border-gray-800">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="mt-3 h-3 w-1/3" />
            </div>
          ))}
        </div>
      ) : currentView === "board" ? (
        <SupportBoard tickets={searched} t={t} language={language} />
      ) : currentView === "calendar" ? (
        <SupportCalendar tickets={searched} t={t} language={language} />
      ) : filtered.length === 0 ? (
        <EmptyState
          t={t}
          onCreate={openCompose}
          hasTickets={(tickets ?? []).length > 0}
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((item) => (
            <TicketRow key={item.name} ticket={item} t={t} language={language} />
          ))}
        </div>
      )}
      </PageContainer>
    </div>
  );
}

// A small self-contained dropdown shell (button + panel + click-outside).
function FilterMenu({
  icon,
  label,
  panelClass,
  children,
}: {
  icon: ReactNode;
  label: ReactNode;
  panelClass?: string;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
          open
            ? "border-gray-300 bg-gray-50 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800",
        )}
      >
        {icon}
        <span className="max-w-[13rem] truncate">{label}</span>
        <ChevronDown className={cn("h-4 w-4 text-gray-400 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div
          className={cn(
            "absolute left-0 z-30 mt-1.5 max-h-[70vh] overflow-y-auto rounded-2xl border border-gray-200 bg-white p-1.5 shadow-lg shadow-gray-900/5 dark:border-gray-700 dark:bg-gray-900",
            panelClass ?? "w-72",
          )}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

// John-only super-admin filter bar: pick a business/stall + which statuses to keep.
function ReceiverFilterBar({
  t,
  tenantGroups,
  tenantSel,
  setTenantSel,
  keptStatuses,
  setKeptStatuses,
  resultCount,
}: {
  t: T;
  tenantGroups: TenantGroup[];
  tenantSel: TenantSel;
  setTenantSel: (s: TenantSel) => void;
  keptStatuses: TicketStatus[];
  setKeptStatuses: (s: TicketStatus[]) => void;
  resultCount: number;
}) {
  const [tenantQuery, setTenantQuery] = useState("");
  const q = tenantQuery.trim().toLowerCase();
  const groups = q
    ? tenantGroups
        .map((g) => ({
          ...g,
          stalls: g.stalls.filter(
            (s) => g.company.toLowerCase().includes(q) || s.name.toLowerCase().includes(q),
          ),
        }))
        .filter((g) => g.company.toLowerCase().includes(q) || g.stalls.length > 0)
    : tenantGroups;

  const statusLabelText = sameSet(keptStatuses, ALL_STATUSES)
    ? "ทุกสถานะ"
    : sameSet(keptStatuses, ACTIVE_STATUSES)
      ? "กำลังดำเนินการ"
      : keptStatuses.length === 0
        ? "ทุกสถานะ"
        : `${keptStatuses.length} สถานะ`;

  const toggleStatus = (s: TicketStatus) =>
    setKeptStatuses(
      keptStatuses.includes(s) ? keptStatuses.filter((x) => x !== s) : [...keptStatuses, s],
    );

  const rowBtn =
    "flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-800";
  const countPill =
    "rounded-full bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400";

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Tenant / stall */}
      <FilterMenu
        icon={<Store className="h-4 w-4 text-gray-400" />}
        label={tenantSel.label}
        panelClass="w-80"
      >
        {(close) => (
          <div>
            <div className="relative p-1.5">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                autoFocus
                placeholder="ค้นหาร้าน / ลูกค้า…"
                value={tenantQuery}
                onChange={(e) => setTenantQuery(e.target.value)}
                className="h-9 pl-9"
              />
            </div>
            <button
              className={cn(rowBtn, "font-medium")}
              onClick={() => {
                setTenantSel({ ids: [], label: ALL_TENANTS_LABEL });
                close();
              }}
            >
              <span>{ALL_TENANTS_LABEL}</span>
              {tenantSel.ids.length === 0 && <Check className="h-4 w-4 text-primary" />}
            </button>
            {groups.map((g) => {
              const groupIds = g.stalls.map((s) => s.id);
              const groupSelected =
                tenantSel.ids.length === groupIds.length &&
                groupIds.every((id) => tenantSel.ids.includes(id));
              return (
                <div key={g.company} className="mt-0.5">
                  <button
                    className={cn(rowBtn, "font-medium text-gray-900 dark:text-gray-100")}
                    onClick={() => {
                      setTenantSel({ ids: groupIds, label: g.company });
                      close();
                    }}
                  >
                    <span className="truncate">{g.company}</span>
                    <span className="flex items-center gap-1.5">
                      <span className={countPill}>{g.count}</span>
                      {groupSelected && <Check className="h-4 w-4 text-primary" />}
                    </span>
                  </button>
                  {g.stalls.length > 1 &&
                    g.stalls.map((s) => (
                      <button
                        key={s.id}
                        className={cn(rowBtn, "pl-6 text-gray-600 dark:text-gray-300")}
                        onClick={() => {
                          setTenantSel({ ids: [s.id], label: s.name });
                          close();
                        }}
                      >
                        <span className="truncate">{s.name}</span>
                        <span className="flex items-center gap-1.5">
                          <span className={countPill}>{s.count}</span>
                          {tenantSel.ids.length === 1 && tenantSel.ids[0] === s.id && (
                            <Check className="h-4 w-4 text-primary" />
                          )}
                        </span>
                      </button>
                    ))}
                </div>
              );
            })}
            {groups.length === 0 && (
              <p className="px-2.5 py-6 text-center text-sm text-gray-400">ไม่พบร้าน</p>
            )}
          </div>
        )}
      </FilterMenu>

      {/* Status (multi-select / "show all except…") */}
      <FilterMenu
        icon={<SlidersHorizontal className="h-4 w-4 text-gray-400" />}
        label={`สถานะ: ${statusLabelText}`}
        panelClass="w-64"
      >
        {() => (
          <div>
            <div className="flex gap-1 p-1">
              <button
                onClick={() => setKeptStatuses(ACTIVE_STATUSES)}
                className="flex-1 rounded-lg bg-gray-100 px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                กำลังดำเนินการ
              </button>
              <button
                onClick={() => setKeptStatuses([...ALL_STATUSES])}
                className="flex-1 rounded-lg bg-gray-100 px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                ทั้งหมด
              </button>
            </div>
            {ALL_STATUSES.map((s) => {
              const on = keptStatuses.includes(s);
              return (
                <button key={s} className={rowBtn} onClick={() => toggleStatus(s)}>
                  <span className="flex items-center gap-2">
                    <span
                      className={cn(
                        "flex h-4 w-4 items-center justify-center rounded border",
                        on
                          ? "border-primary bg-primary text-white"
                          : "border-gray-300 dark:border-gray-600",
                      )}
                    >
                      {on && <Check className="h-3 w-3" />}
                    </span>
                    {statusLabel(t, s)}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </FilterMenu>

      <span className="ml-auto text-xs text-gray-400">{resultCount} รายการ</span>
    </div>
  );
}

function TicketRow({
  ticket,
  t,
  language,
}: {
  ticket: TicketSummary;
  t: T;
  language: string;
}) {
  return (
    <Link
      to={`/support/${ticket.name}`}
      className="group block rounded-2xl border border-gray-200 bg-white p-4 transition-all hover:border-gray-300 hover:shadow-sm dark:border-gray-800 dark:bg-gray-900/40 dark:hover:border-gray-700"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {ticket.unread && (
              <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-label={t.newReply} />
            )}
            <h3 className="truncate font-medium text-gray-900 dark:text-gray-100">
              {ticket.subject}
            </h3>
          </div>
          <p className="mt-1 truncate text-sm text-gray-500 dark:text-gray-400">
            {ticket.lastMessagePreview}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400 dark:text-gray-500">
            {(ticket as TicketSummary & { tenantName?: string }).tenantName && (
              <>
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 font-medium text-blue-600 dark:bg-blue-500/10 dark:text-blue-300">
                  {(ticket as TicketSummary & { tenantName?: string }).tenantName}
                </span>
                <span className="text-gray-300 dark:text-gray-600">·</span>
              </>
            )}
            <span className="font-mono">{ticket.name}</span>
            <span className="text-gray-300 dark:text-gray-600">·</span>
            <span>{categoryLabel(t, ticket.category)}</span>
            <span className="text-gray-300 dark:text-gray-600">·</span>
            <PriorityBadge priority={ticket.priority} label={priorityLabel(t, ticket.priority)} />
            <span className="text-gray-300 dark:text-gray-600">·</span>
            <span className="inline-flex items-center gap-1">
              <MessageSquare className="h-3 w-3" />
              {ticket.messageCount}
            </span>
            <span className="text-gray-300 dark:text-gray-600">·</span>
            <span>{t.updatedAgo.replace("{{time}}", relativeTime(ticket.lastActivityOn, language))}</span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {(ticket as TicketSummary & { internalStatus?: string }).internalStatus && (
            <span
              className="hidden rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-600 sm:inline dark:bg-indigo-500/10 dark:text-indigo-300"
              title="สถานะภายใน (ทีม/AI)"
            >
              {INTERNAL_STATUS_TH[(ticket as TicketSummary & { internalStatus?: string }).internalStatus as string] ??
                (ticket as TicketSummary & { internalStatus?: string }).internalStatus}
            </span>
          )}
          <StatusPill status={ticket.status} label={statusLabel(t, ticket.status)} />
          <ChevronRight className="h-4 w-4 text-gray-300 transition-transform group-hover:translate-x-0.5 dark:text-gray-600" />
        </div>
      </div>
    </Link>
  );
}

function EmptyState({
  t,
  onCreate,
  hasTickets,
}: {
  t: T;
  onCreate: () => void;
  hasTickets: boolean;
}) {
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 py-16 text-center dark:border-gray-800">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 dark:bg-gray-800">
        <LifeBuoy className="h-6 w-6 text-gray-400" />
      </div>
      <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
        {hasTickets ? t.emptyNoMatchTitle : t.emptyNoTicketsTitle}
      </h3>
      <p className="mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
        {hasTickets ? t.emptyNoMatchDesc : t.emptyNoTicketsDesc}
      </p>
      {!hasTickets && (
        <Button className="mt-5 rounded-full" onClick={onCreate}>
          <Plus className="h-4 w-4" />
          {t.newRequest}
        </Button>
      )}
    </div>
  );
}
